/**
 * Mikrofon aufnehmen, bis der Techniker eine Pause macht — als WAV für die Transkription.
 *
 * Sprachende per Pegel: In den ersten 300 ms lernt die Aufnahme den Grundpegel (Auto, Werkstatt,
 * Serverraum), danach gilt als Sprache, was deutlich darüber liegt. Nach `silenceMs` Stille ist
 * die Aussage fertig. Das Mikrofon wird nach jeder Aussage freigegeben — sonst bliebe am Handy
 * der Hörer statt des Lautsprechers aktiv, und KIANs Antwort wäre kaum zu hören.
 */
import { WAV_SAMPLE_RATE, concatFloat32, downsample, encodeWav, rms } from './wav';

export interface CaptureOptions {
  /** Stille nach dem Sprechen, nach der die Aussage fertig ist */
  silenceMs?: number;
  /** Längste Aussage */
  maxMs?: number;
  /** Ohne Sprache so lange warten, dann aufgeben */
  noSpeechMs?: number;
  /** Pegel 0 … 1 für die Anzeige */
  onLevel?: (level: number) => void;
  /** Erste Sprache erkannt */
  onSpeech?: () => void;
}

export interface CaptureResult {
  /** null, wenn nichts gesagt oder verworfen wurde */
  wav: Blob | null;
  heardSpeech: boolean;
  durationMs: number;
  reason: 'silence' | 'max' | 'stopped' | 'no-speech' | 'cancelled';
}

export interface CaptureSession {
  done: Promise<CaptureResult>;
  /** Aufnahme beenden und auswerten („Fertig gesprochen") */
  stop(): void;
  /** Aufnahme verwerfen */
  cancel(): void;
}

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext;
}

export function isCaptureSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!audioContextCtor();
}

/** Muss aus einem Klick heraus entstehen — sonst bleibt der Ton auf dem iPhone stumm. */
export function createAudioContext(): AudioContext {
  const Ctor = audioContextCtor();
  if (!Ctor) throw new Error('Audio wird von diesem Browser nicht unterstützt.');
  return new Ctor();
}

/** Grundpegel-Lernphase */
const CALIBRATE_MS = 300;

export async function startCapture(ctx: AudioContext, opts: CaptureOptions = {}): Promise<CaptureSession> {
  const silenceMs = opts.silenceMs ?? 1300;
  const maxMs = opts.maxMs ?? 60_000;
  const noSpeechMs = opts.noSpeechMs ?? 12_000;

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
  });
  if (ctx.state === 'suspended') await ctx.resume().catch(() => {});

  const source = ctx.createMediaStreamSource(stream);
  // ScriptProcessor statt AudioWorklet: läuft überall (auch Safari) ohne eigenes Modul
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(processor);
  processor.connect(mute);
  mute.connect(ctx.destination);

  const chunks: Float32Array[] = [];
  const started = performance.now();
  let noiseFloor = 0;
  let calibrationBlocks = 0;
  let heard = false;
  let firstSpeechChunk = -1;
  let lastVoiceAt = 0;
  let finished = false;
  let resolveDone!: (r: CaptureResult) => void;
  const done = new Promise<CaptureResult>((resolve) => {
    resolveDone = resolve;
  });

  const cleanup = () => {
    processor.onaudioprocess = null;
    try {
      source.disconnect();
      processor.disconnect();
      mute.disconnect();
    } catch {
      /* schon getrennt */
    }
    stream.getTracks().forEach((t) => t.stop());
  };

  const finish = (reason: CaptureResult['reason'], keep: boolean) => {
    if (finished) return;
    finished = true;
    cleanup();
    opts.onLevel?.(0);
    const durationMs = performance.now() - started;
    if (!keep || !heard) {
      resolveDone({ wav: null, heardSpeech: heard, durationMs, reason });
      return;
    }
    // Etwas Vorlauf vor der ersten Sprache behalten, die lange Stille davor nicht
    const from = Math.max(0, firstSpeechChunk - 2);
    const samples = downsample(concatFloat32(chunks.slice(from)), ctx.sampleRate, WAV_SAMPLE_RATE);
    resolveDone({ wav: encodeWav(samples, WAV_SAMPLE_RATE), heardSpeech: true, durationMs, reason });
  };

  processor.onaudioprocess = (e: AudioProcessingEvent) => {
    if (finished) return;
    const block = new Float32Array(e.inputBuffer.getChannelData(0));
    chunks.push(block);
    const level = rms(block);
    const now = performance.now();
    const elapsed = now - started;
    opts.onLevel?.(Math.min(1, level / 0.12));

    if (elapsed < CALIBRATE_MS) {
      noiseFloor = (noiseFloor * calibrationBlocks + level) / (calibrationBlocks + 1);
      calibrationBlocks++;
      return;
    }
    const threshold = Math.max(0.012, noiseFloor * 2.5);
    if (level > threshold) {
      if (!heard) {
        heard = true;
        firstSpeechChunk = chunks.length - 1;
        opts.onSpeech?.();
      }
      lastVoiceAt = now;
    } else if (!heard) {
      // Grundpegel langsam nachführen, solange noch niemand spricht
      noiseFloor = noiseFloor * 0.97 + level * 0.03;
    }

    if (heard && now - lastVoiceAt > silenceMs) finish('silence', true);
    else if (!heard && elapsed > noSpeechMs) finish('no-speech', false);
    else if (elapsed > maxMs) finish('max', true);
  };

  return {
    done,
    stop: () => finish('stopped', true),
    cancel: () => finish('cancelled', false),
  };
}
